import React, { useState, useEffect } from 'react';
import { IonContent, IonPage, IonButton, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonList, IonAlert } from '@ionic/react';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import NavBar from './NavBar';

if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
  throw new Error('Missing Stripe public key');
}

// Initialize Stripe with the public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const PaymentGateway: React.FC = () => {
  const { t } = useTranslation();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [user] = useAuthState(auth);

  const subscriptionPlans = [
    {
      id: 'monthly_sub',
      name: t('paymentGateway.subscriptionPlans.monthly.title'),
      period: '1 month',
      price: '€15',
      pricePerMonth: t('paymentGateway.subscriptionPlans.monthly.price'),
      description: t('paymentGateway.subscriptionPlans.monthly.description'),
      stripePriceId: 'price_1QFMSyJUiYvIMTIyzoAT78z4'
    },
    {
      id: 'quarterly_sub',
      name: t('paymentGateway.subscriptionPlans.quarterly.title'),
      period: '3 months',
      price: '€36',
      pricePerMonth: t('paymentGateway.subscriptionPlans.quarterly.price'),
      description: t('paymentGateway.subscriptionPlans.quarterly.description'),
      stripePriceId: 'price_1QFMVfJUiYvIMTIyf8jm5Y7Y',
      isPopular: true,
      savings: t('paymentGateway.subscriptionPlans.quarterly.savings')
    },
    {
      id: 'annual_sub',
      name: t('paymentGateway.subscriptionPlans.annual.title'),
      period: '12 months',
      price: '€108',
      pricePerMonth: t('paymentGateway.subscriptionPlans.annual.price'),
      description: t('paymentGateway.subscriptionPlans.annual.description'),
      savings: t('paymentGateway.subscriptionPlans.annual.savings'),
      stripePriceId: 'price_1QFMYQJUiYvIMTIyvEbb9HOx'
    }
  ];

  useEffect(() => {
    checkSubscriptionStatus();
  }, [user]);

  const checkSubscriptionStatus = async () => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        setIsSubscribed(userDoc.data().subscriptionStatus === 'active');
      }
    }
  };

  const handleSubscription = async (planId: string) => {
    if (!user) {
      setAlertMessage(t('paymentGateway.userNotAuthenticated'));
      setShowAlert(true);
      return;
    }

    setIsProcessing(true);

    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe failed to initialize');

      const plan = subscriptionPlans.find(p => p.id === planId);
      if (!plan) throw new Error('Plan not found');

      // Rediriger directement vers Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        mode: 'subscription',
        lineItems: [
          {
            price: plan.stripePriceId,
            quantity: 1,
          },
        ],
        successUrl: `${window.location.origin}/payment/success`,
        cancelUrl: `${window.location.origin}/payment/cancel`,
        customerEmail: user.email || undefined,
      });

      if (error) {
        throw new Error(error.message);
      }

    } catch (error: any) {
      console.error('Error processing subscription:', error);
      setAlertMessage(t('paymentGateway.error'));
      setShowAlert(true);
      setIsProcessing(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <h1 className="text-2xl font-bold mb-4">{t('paymentGateway.title').replace('Russian Tutor AI', 'LangueWise')}</h1>
        
        {isSubscribed ? (
          <div className="text-center p-4 bg-green-100 rounded-lg mb-4">
            <h2 className="text-xl font-bold text-green-700">{t('dashboard.activeSubscription')}</h2>
          </div>
        ) : (
          <IonList class='pb-32'>
            {subscriptionPlans.map((plan) => (
              <IonCard 
                key={plan.id} 
                className={`mb-4 relative ${plan.isPopular ? 'border-2 border-blue-500 transform scale-105' : ''}`}
              >
                {plan.isPopular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white px-4 py-1 rounded-bl-lg font-bold">
                    {t('paymentGateway.mostPopular')}
                  </div>
                )}
                <IonCardHeader>
                  <IonCardTitle className="text-xl font-bold">{plan.name}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="mb-2">
                    <p className="text-2xl font-bold text-primary">{plan.price}</p>
                    <p className="text-sm text-gray-600">{plan.pricePerMonth}</p>
                    {plan.savings && (
                      <p className="text-sm font-bold text-green-600">{plan.savings}</p>
                    )}
                  </div>
                  <ul className="list-disc pl-5 mb-4">
                    <li>{plan.description}</li>
                    <li>{t('paymentGateway.features.ai')}</li>
                    <li>{t('paymentGateway.features.personalized')}</li>
                    <li>{t('paymentGateway.features.support')}</li>
                  </ul>
                  <IonButton
                    expand="block"
                    onClick={() => handleSubscription(plan.id)}
                    disabled={isProcessing}
                    className={plan.isPopular ? 'ion-color-success' : ''}
                  >
                    {isProcessing ? t('paymentGateway.processing') : t('paymentGateway.subscribe')}
                  </IonButton>
                </IonCardContent>
              </IonCard>
            ))}
          </IonList>
        )}

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('common.success')}
          message={alertMessage}
          buttons={[t('common.ok')]}
        />

        <NavBar />
      </IonContent>
    </IonPage>
  );
};

export default PaymentGateway;
